<template>
  <div>
    <Header>Vehicle List</Header>
    <div class="tiled-bar-image"></div>
    <Main>
      <div class="mt-3 mb-3 text-left p-3 bg-gray-100 rounded-md !shadow relative">
        <AdvancedSearchForm
          v-model="filters"
          :listName="'vehicle'"
          :searchInput="searchInput"
          @filtersUpdated="onFiltersUpdated"
          :searchbarListOverride="searchbarList"
        />
      </div>
      <div class="flex relative justify-center items-center mt-4" v-if="vehicles.length > 0">
        <Button v-if="canSubmitNew"
          :color="'blue'"
          :size="'md'"
          class="new-vehicle-button absolute-right rounded-md !shadow m-1"
          @click="toVehicleForm()">
          New Vehicle
        </Button>
        <Pagination :pages="pages" to="/VehicleList" />
      </div>
      <Spinner
        v-if="vehiclesLoading"
        message="Loading Vehicles"
        :size="16"
      />
      <Table class="relative left-[calc(-40vw+50%)] w-[80vw]">
        <template #columns>
          <th
            v-for="(column, index) in columns"
            :key="index"
            scope="col"
            class="px-2 py-3 text-left text-xs text-gray-500 uppercase tracking-wider"
          >
            <Sort v-if="column.sortable"
              class="px-2 py-3 text-left text-xs text-gray-500 uppercase tracking-wider"
              :key="index"
              :name="column.name"
              :outlined="false"
              :selectedSort="selectedSort"
              @setSelectedSort="selectedSort = $event"
            >
              {{ column.text }}
            </Sort>
            <div v-else>
              {{ column.text }}
            </div>
          </th>
        </template>
        <template #rows>
          <VehicleItem
            v-for="(vehicle, index) in vehicles"
            :key="index"
            :vehicle="vehicle"
            :showInternalData="showInternalData"
          />
        </template>
      </Table>
      <div class="flex justify-center pb-4">
        <Pagination :pages="pages" to="/VehicleList" />
      </div>
    </Main>
  </div>
</template>

<style scoped>
.tiled-bar-image {
  width: 100%;
  height: 25px;
  background-image: url("@/assets/Bar.png");
  background-repeat: repeat-x;
}
.new-vehicle-button {
  position: absolute;
  right: 0;
  color: #616060;
  font-size: 14px;
  margin-top: 5px;
}
</style>

<script>
import vehicleListService from '../services/pages/vehicleList.js'

import view from '../services/user/view.js'
import user from '../services/user/user.js'
import events from '../services/events.js'

import Header from '../components/Header.vue'
import Main from '../components/Main.vue'
import Button from '../components/Button.vue'
import Pagination from '../components/Pagination.vue'
import Spinner from '../components/Spinner.vue'
import Table from '../components/Table.vue'
import Sort from '../components/Sort.vue'
import VehicleItem from '../components/items/VehicleItem.vue'
import AdvancedSearchForm from '../components/Search/AdvancedSearchForm.vue'

import DropdownLists from '../components/DropdownLists.json'
import { omit, isEqual, forOwn } from 'lodash'
import fastDeepEquals from 'fast-deep-equal'

export default {
  components: {
    Header,
    Main,
    Button,
    Pagination,
    Spinner,
    Table,
    Sort,
    VehicleItem,
    AdvancedSearchForm,
  },
  data() {
    return {
      user: undefined,
      view: undefined,

      canSubmitNew: false,

      query: {
        search: '',
      },
      filters: [],
      vehicles: [],
      totalVehicles: 0,
      vehiclesLoading: false,
      showInternalData: false,

      pages: {},
      numPerPage: 50,

      selectedSort: {
        name: '',
        descending: false,
      },
      searchInput: undefined,
      searchbarList: DropdownLists.VehicleSearchbarList,
      columns: [
        {
          name: 'license_plate',
          text: 'License Plate',
          sortable: true,
        },
        {
          name: 'vin',
          text: 'VIN',
          sortable: true,
        },
        {
          name: 'make',
          text: 'Make',
          sortable: true,
        },
        {
          name: 'model',
          text: 'Model',
          sortable: true,
        },
        {
          name: 'color',
          text: 'Color',
          sortable: true,
        },
        {
          name: 'capacity',
          text: 'Capacity',
          sortable: true
        }
      ]
    }
  },
  eventCallbackIds: {},
  methods: {
    async setVehicleData() {
      this.view = await view.get()

      this.numPerPage = vehicleListService.getNumPerPage()

      let apiParams = {
        perPage: this.numPerPage,
        page: this.$route.query.page,
        search: this.query.search,
        sortBy: this.selectedSort.name
          ? ((this.selectedSort.descending ? '-' : '') + this.selectedSort.name)
          : '',
      }

      let vehiclesData
      try {
        this.vehiclesLoading = true
        vehiclesData = (await vehicleListService.getVehicleDataFromServer(apiParams)).data
        this.vehiclesLoading = false
      }
      catch {
        this.vehiclesLoading = false
        return
      }

      this.pages = vehiclesData.pages
      this.vehicles = vehiclesData.content
      this.totalVehicles = vehiclesData.page_data.num_vehicles
    },
    setQuerySearch(search) {
      if (Object.hasOwn(this.$route.query, 'search')) {
        this.query.search = search
      }
      else {
        delete this.query.search
      }
    },
    setQueryPage(page) {
      if (Object.hasOwn(this.$route.query, 'page')) {
        this.query.page = page
      }
      else {
        delete this.query.page
      }
    },
    updateQueryParams(queryObj) {
      if (!fastDeepEquals(queryObj, this.$route.query)) {
        this.$router.push({
          query: queryObj
        })
        return true
      }
      else {
        return false
      }
    },
    setFiltersQuery() {
      let queryObj = {
        query: {
          ...this.$route.query
        }
      }
      if (this.query.search) {
        queryObj.query.search = this.query.search
      }
      else {
        delete queryObj.query.search
      }

      this.updateQueryParams(queryObj.query)
    },
    setSearchQueryFromQueryParams() {
      forOwn(this.$route.query, (value, key) => {
        switch (key) {
          case 'search':
            this.searchInput = value
            this.setQuerySearch(value)
            break
          case 'page':
            this.setQueryPage(value)
            break
          case 'sortByName':
            this.selectedSort.name = value
            break
          case 'sortByDescending':
            this.selectedSort.descending = value === 'true'
            break
          case 'filters':
            try {
              this.filters = JSON.parse(value)
            } catch {
              this.filters = []
            }
            break
        }
      })
      if (!Object.hasOwn(this.$route.query, 'search')) {
        delete this.query.search
      }
    },
    onFiltersUpdated() {
      if (this.filters.length === 0) {
        delete this.query.search
      }
      else if (this.filters.length === 1) {
        if (!this.filters[0].keyword) {
          delete this.query.search
        }
        else if (this.filters[0].field) {
          this.query.search = JSON.stringify({
            keyword: this.filters[0].keyword,
            field: this.filters[0].field
          })
        }
        else {
          this.query.search = this.filters[0].keyword
        }
      }
      else if (this.filters.length > 1) {
        this.query.search = JSON.stringify(this.filters)
      }
      this.setFiltersQuery()
    },
    checkUserCanViewPage() {
      if (!this.user.can('view_vehicle_list')) {
        this.$router.push({ path: '/' })
        return true
      }
    },
    toVehicleForm() {
      this.$router.push({ name: 'VehicleForm' })
    },
  },
  async created() {
    this.user = await user.get()
    if (this.checkUserCanViewPage()) {
      return
    }

    this.canSubmitNew = this.user.can('submit_new_vehicle')

    this.$options.eventCallbackIds.listPageSettingsUpdate =
      events.register('listPageSettingsUpdate', (currNumPerPage) => {
      if (this.numPerPage != currNumPerPage) {
        this.setVehicleData()
      }
    })
    this.$options.eventCallbackIds.viewUpdated = events.register('viewUpdated', async () => {
      this.setVehicleData()
    })

    this.setSearchQueryFromQueryParams()
    this.setVehicleData()
  },
  async mounted() {
    this.user = await user.get()
    if (vehicleListService.canViewInternalCstData(this.user)){
      this.showInternalData = true
      this.columns.splice(2, 0, 
      {
          name: 'vendor__name',
          text: 'Vendor',
          sortable: true,
        },
      )
    }
  },
  beforeDestroy() {
    events.remove('listPageSettingsUpdate', this.$options.eventCallbackIds.listPageSettingsUpdate)
    events.remove('viewUpdated', this.$options.eventCallbackIds.viewUpdated)
  },
  computed: {

  },
  watch: {
    '$route.query': function () {
      this.setSearchQueryFromQueryParams()
      this.setVehicleData()
    },
    selectedSort: {
      handler() {
        let query = omit(this.$route.query, ['sortByName', 'sortByDescending'])
        if (this.selectedSort.name) {
          query.sortByName = this.selectedSort.name
          query.sortByDescending = this.selectedSort.descending
        }
        const queryCompare = {
          ...query,
          sortByDescending: query.sortByDescending ? 'true' : 'false'
        }
        if (!isEqual(this.$route.query, queryCompare)) {
          this.$router.replace({
            query: query
          })
        }
      },
      deep: true
    },
  }
}
</script>
